import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Button from '../components/Button'
import CompanySettingsTab from '../components/CompanySettingsTab'
import { useUser } from '../contexts/UserContext'
import { userService } from '../services/userService'
import { UserType } from '../types/user'
import {
  type PushStatus,
  disablePush,
  enablePush,
  getPushStatus,
} from '../utils/pushNotifications'

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useUser()
  const [activeTab, setActiveTab] = useState<'profile' | 'company'>('profile')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [pushStatus, setPushStatus] = useState<PushStatus | 'loading'>(
    'loading',
  )
  const [isPushUpdating, setIsPushUpdating] = useState(false)

  // Check browser push notification status on mount
  useEffect(() => {
    let cancelled = false
    getPushStatus().then((status) => {
      if (!cancelled) {
        setPushStatus(status)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Check if user is an owner
  const isOwner =
    user?.user_type === UserType.OWNER || user?.user_type === 'OWNER'

  // Format phone number to work with django-phonenumber-field
  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, '')
    const withoutCountryCode = digits.startsWith('1') ? digits.slice(1) : digits
    if (withoutCountryCode.length !== 10) {
      throw new Error('Phone number must be 10 digits')
    }
    return `+1${withoutCountryCode}`
  }

  // Pre-populate form with user data
  useEffect(() => {
    if (user) {
      const digits = (user.phone_number || '').replace(/\D/g, '')
      const withoutCountryCode = digits.startsWith('1')
        ? digits.slice(1)
        : digits
      const displayPhone =
        withoutCountryCode.length === 10
          ? `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`
          : user.phone_number || ''

      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phoneNumber: displayPhone,
      })
      setPhoneError(null)
    }
  }, [user])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (field === 'phoneNumber') {
      try {
        if (value.replace(/\D/g, '').length >= 10) {
          formatPhoneNumber(value)
          setPhoneError(null)
        } else {
          setPhoneError(null)
        }
      } catch (err) {
        setPhoneError(
          err instanceof Error ? err.message : 'Invalid phone number',
        )
      }
    }
  }

  const handleCancel = () => {
    if (!user) return
    const digits = (user.phone_number || '').replace(/\D/g, '')
    const withoutCountryCode = digits.startsWith('1') ? digits.slice(1) : digits
    const displayPhone =
      withoutCountryCode.length === 10
        ? `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`
        : user.phone_number || ''

    setFormData({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
      phoneNumber: displayPhone,
    })
    setPhoneError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    try {
      setIsSubmitting(true)
      setPhoneError(null)

      const formattedPhone = formData.phoneNumber
        ? formatPhoneNumber(formData.phoneNumber)
        : undefined

      await userService.updateUser(user.id, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formattedPhone,
      })

      await refreshUser()
      toast.success('Profile updated successfully')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update profile'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePushToggle = async () => {
    try {
      setIsPushUpdating(true)
      if (pushStatus === 'subscribed') {
        await disablePush()
        toast.success('Browser notifications disabled')
      } else {
        await enablePush()
        toast.success('Browser notifications enabled')
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to update browser notifications'
      toast.error(message)
    } finally {
      setIsPushUpdating(false)
      setPushStatus(await getPushStatus())
    }
  }

  const renderPushSection = () => (
    <div
      className={css(styles.formCard, styles.pushSection)}
      data-testid="push-notification-section"
    >
      <div className={css(styles.cardHeader)}>
        <h2 className={css(styles.cardTitle)}>Browser Notifications</h2>
        <p className={css(styles.cardSubtitle)}>
          Get notified about new tags, work orders, and invoices even when the
          app is closed
        </p>
      </div>

      <div className={css(styles.pushContent)}>
        {pushStatus === 'loading' && (
          <p className={css(styles.pushMutedText)}>
            Checking notification status...
          </p>
        )}
        {pushStatus === 'unsupported' && (
          <p className={css(styles.pushMutedText)}>
            Browser notifications aren't available in this browser.
          </p>
        )}
        {pushStatus === 'denied' && (
          <p className={css(styles.pushStatusText)}>
            Notifications are blocked. To enable them, allow notifications for
            this site in your browser settings.
          </p>
        )}
        {(pushStatus === 'subscribed' || pushStatus === 'unsubscribed') && (
          <div className={css(styles.pushRow)}>
            <p className={css(styles.pushStatusText)}>
              Browser notifications are{' '}
              {pushStatus === 'subscribed' ? 'enabled' : 'disabled'}.
            </p>
            <button
              type="button"
              className={css(styles.pushToggleButton)}
              disabled={isPushUpdating}
              onClick={handlePushToggle}
              data-testid="push-toggle-button"
            >
              {isPushUpdating
                ? 'Updating...'
                : pushStatus === 'subscribed'
                  ? 'Disable'
                  : 'Enable'}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderProfileContent = () => (
    <div className={css(styles.formCard)}>
      <div className={css(styles.cardHeader)}>
        <h2 className={css(styles.cardTitle)}>Personal Information</h2>
        <p className={css(styles.cardSubtitle)}>
          Update your personal details and contact information
        </p>
      </div>

      <form onSubmit={handleSubmit} className={css(styles.form)}>
        <div className={css(styles.formRow)}>
          <div className={css(styles.formGroup)}>
            <label htmlFor="firstName" className={css(styles.label)}>
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={css(styles.input)}
              placeholder="Enter your first name"
            />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="lastName" className={css(styles.label)}>
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={css(styles.input)}
              placeholder="Enter your last name"
            />
          </div>
        </div>

        <div className={css(styles.formGroup)}>
          <label htmlFor="email" className={css(styles.label)}>
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={css(styles.input)}
            placeholder="Enter your email address"
          />
        </div>

        <div className={css(styles.formGroup)}>
          <label htmlFor="phoneNumber" className={css(styles.label)}>
            Phone Number
          </label>
          <input
            id="phoneNumber"
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            className={css(styles.input)}
            placeholder="Enter your phone number"
          />
          {phoneError && (
            <span className={css(styles.errorText)}>{phoneError}</span>
          )}
        </div>

        <div className={css(styles.formActions)}>
          <button
            type="button"
            className={css(styles.cancelButton)}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <Button
            type="submit"
            className={css(styles.saveButton)}
            disabled={isSubmitting || !!phoneError}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.title)}>
            {activeTab === 'profile' ? 'Profile Settings' : 'Company Settings'}
          </h1>
          <p className={css(styles.subtitle)}>
            {activeTab === 'profile'
              ? 'Manage your personal information and account settings'
              : 'Manage your company information and billing settings'}
          </p>
        </div>
      </div>

      <div className={css(styles.contentWrapper)}>
        {/* Tab Navigation - Only show if user is an owner */}
        {isOwner && (
          <div className={css(styles.tabNavigation)}>
            <button
              type="button"
              className={css(
                styles.tabButton,
                activeTab === 'profile' && styles.activeTab,
              )}
              onClick={() => setActiveTab('profile')}
            >
              <i className="fa-regular fa-user" />
              Profile
            </button>
            <button
              type="button"
              className={css(
                styles.tabButton,
                activeTab === 'company' && styles.activeTab,
              )}
              onClick={() => setActiveTab('company')}
            >
              <i className="fa-regular fa-building" />
              Company
            </button>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'profile' ? (
          <>
            {renderProfileContent()}
            {renderPushSection()}
          </>
        ) : (
          <div className={css(styles.formCard)}>
            <CompanySettingsTab />
          </div>
        )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
    padding: '0',
  },
  header: {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E5E7EB',
    padding: '24px 32px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#111928',
    margin: '0 0 4px 0',
    fontFamily: 'Inter, sans-serif',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
    fontFamily: 'Inter, sans-serif',
  },
  contentWrapper: {
    maxWidth: '800px',
    margin: '32px auto',
    padding: '0 32px',
  },
  tabNavigation: {
    display: 'flex',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '4px',
    marginBottom: '24px',
    gap: '2px',
  },
  tabButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6B7280',
    fontFamily: 'Inter, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#374151',
    },
  },
  activeTab: {
    backgroundColor: '#FFFFFF !important',
    color: '#1C64F2 !important',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    ':hover': {
      backgroundColor: '#FFFFFF !important',
      color: '#1C64F2 !important',
    },
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '24px 32px',
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111928',
    margin: '0 0 4px 0',
    fontFamily: 'Inter, sans-serif',
  },
  cardSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
    fontFamily: 'Inter, sans-serif',
  },
  form: {
    padding: '32px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
      gap: '20px',
    },
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '24px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
    fontFamily: 'Inter, sans-serif',
  },
  input: {
    padding: '12px 16px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111928',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 3px rgba(28, 100, 242, 0.1)',
    },
    ':hover': {
      borderColor: '#D1D5DB',
    },
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#1C64F2',
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    cursor: 'pointer',
    padding: '12px 16px',
    borderRadius: '8px',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: 'rgba(28, 100, 242, 0.1)',
    },
  },
  saveButton: {
    backgroundColor: '#1C64F2',
    color: '#FFFFFF',
    ':hover': {
      backgroundColor: '#1E40AF',
    },
  },
  errorText: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '6px',
    fontFamily: 'Inter, sans-serif',
  },
  pushSection: {
    marginTop: '24px',
  },
  pushContent: {
    padding: '24px 32px',
  },
  pushRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  pushStatusText: {
    fontSize: '14px',
    color: '#374151',
    margin: 0,
    fontFamily: 'Inter, sans-serif',
  },
  pushMutedText: {
    fontSize: '14px',
    color: '#9CA3AF',
    margin: 0,
    fontFamily: 'Inter, sans-serif',
  },
  pushToggleButton: {
    backgroundColor: '#1C64F2',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#1E40AF',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
})

export default ProfilePage
